import { Environment, Sky, useScroll } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as React from "react";
import * as THREE from "three";
import { random } from "maath";
import {
  DEFAULT_FOG_COLOR,
  DEFAULT_FOG_FAR,
  DEFAULT_FOG_NEAR,
  DECKS_BG_COLOR,
  DECKS_FOG_COLOR,
  DECKS_FOG_FAR,
  HERO_SKY_DISTANCE,
  HERO_SKY_SUN_POSITION,
  SERVICES_FOG_COLOR,
} from "./constants";
import { clamp01 } from "./utils";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useGpuTier } from "../../hooks/useGpuTier";
import { useSceneScalars } from "../../hooks/useSceneScalars";
import { cloudSegmentScale } from "./perf";
import { HomePart } from "./parts/HomePart";
import { DecksPart } from "./parts/DecksPart";
import { ServicesPart } from "./parts/ServicesPart";
import { ReviewsPart } from "./parts/ReviewsPart";
import type { SceneKey } from "./ScrollSceneController";

export type SectionRefs = {
  home: React.RefObject<THREE.Group>;
  services: React.RefObject<THREE.Group>;
  decks: React.RefObject<THREE.Group>;
  reviews: React.RefObject<THREE.Group>;
};

type Props = {
  /** Which section is "active" (opacity/emphasis). When sectionRefs is provided, visibility is controlled by refs instead and all parts get active=true. */
  activeScene?: SceneKey;
  /** When provided, each part is wrapped in a group with this ref so parent can control visibility in useFrame (for FBO). All parts then get active=true so the visible one renders at full opacity. */
  sectionRefs?: SectionRefs;
};

export function Scene({ activeScene = "home", sectionRefs }: Props) {
  const spherical = React.useMemo(() => {
    const box = random.inBox(new Float32Array(100 * 3), { sides: [1, 1, 1] });
    return random.onSphere(box, { radius: 1 }) as Float32Array;
  }, []);
  const breakpoint = useBreakpoint();
  const gpuTier = useGpuTier();
  const scalars = useSceneScalars();
  const segments = Math.max(
    1,
    Math.round((spherical.length / 3) * cloudSegmentScale(breakpoint, gpuTier)),
  );


  const useFboMode = sectionRefs != null;
  const showHome = useFboMode ? true : activeScene === "home";
  const showServices = useFboMode ? true : activeScene === "services";
  const showDecks = useFboMode ? true : activeScene === "decks";
  const showReviews = useFboMode ? true : activeScene === "reviews";

  const wrap = (key: SceneKey, active: boolean, children: React.ReactNode) => {
    if (sectionRefs) {
      const ref = sectionRefs[key];
      return <group ref={ref}>{children}</group>;
    }
    return <>{children}</>;
  };

  // Reviews has no dedicated canvas look — its HTML section overlays the decks
  // scene with an opaque white background, so visually the canvas stays in
  // decks mode through the transition.
  const useDecksLook =
    activeScene === "decks" || activeScene === "reviews";

  const { scene } = useThree();
  const scroll = useScroll();

  // Drive the shared scene fog from the light home haze to a darker services
  // dusk so white overlay copy stays legible over the (fully fogged) sky.
  // The fog object is shared across both FBO render passes, so the only way to
  // differ home vs services is over scroll time — ramp across the home→services
  // transition and hold dark through services. Decks keeps its own fog (set via
  // the <fog> JSX args below), so skip the imperative write there.
  const homeFogColor = React.useMemo(() => new THREE.Color(DEFAULT_FOG_COLOR), []);
  const servicesFogColor = React.useMemo(
    () => new THREE.Color(SERVICES_FOG_COLOR),
    [],
  );
  useFrame(() => {
    if (useDecksLook) return;
    const fog = scene.fog as THREE.Fog | null;
    if (!fog) return;
    const t = Math.min(scroll.offset * scalars.tScale, scalars.tMax);
    const p = clamp01(t / scalars.thresholds.homeServices);
    fog.color.copy(homeFogColor).lerp(servicesFogColor, p);
  });
  // Imperative scene.background — `<color attach="background">` doesn't reliably
  // override what Sky/Environment drei components leave behind, especially when
  // toggled conditionally. Setting on the Scene object directly is bulletproof.
  React.useEffect(() => {
    const prev = scene.background;
    if (useDecksLook) {
      scene.background = new THREE.Color(DECKS_BG_COLOR);
      return () => {
        scene.background = prev;
      };
    }
    return undefined;
  }, [useDecksLook, scene]);

  return (
    <>
      <fog
        attach="fog"
        args={[
          useDecksLook ? DECKS_FOG_COLOR : DEFAULT_FOG_COLOR,
          DEFAULT_FOG_NEAR,
          useDecksLook ? DECKS_FOG_FAR : DEFAULT_FOG_FAR,
        ]}
      />
      {!useDecksLook && (
        <Sky distance={HERO_SKY_DISTANCE} sunPosition={HERO_SKY_SUN_POSITION} />
      )}
      {/* Image-based lighting — "city" matches the gate/portal mood on
          home/services; decks (and reviews, which overlays decks) swap to a
          softer "park" preset.

          Wrapped in its own Suspense: switching the preset on entering decks
          fetches the "park" HDR lazily (it isn't covered by the initial
          Loader's preload like the cloud/box/card assets are). Without an inner
          boundary that suspension bubbles to the single Canvas-wide
          <Suspense fallback={null}> in App.js, which blanks the whole tree —
          including the FBO cross-fade quad — to the renderer's black clear
          color for the frame the HDR loads. That is the "big black square"
          flash over the 3D decks. Isolating it here keeps the previous IBL and
          all sibling geometry on screen while the new preset resolves. */}
      <React.Suspense fallback={null}>
        <Environment preset={useDecksLook ? "park" : "city"} />
      </React.Suspense>

      {wrap(
        "home",
        showHome,
        <HomePart
          spherical={spherical}
          segments={segments}
          active={showHome}
        />,
      )}
      {wrap(
        "services",
        showServices,
        <ServicesPart
          spherical={spherical}
          segments={segments}
          active={showServices}
        />,
      )}
      {wrap(
        "decks",
        showDecks,
        <DecksPart
          spherical={spherical}
          segments={segments}
          active={showDecks}
        />,
      )}
      {wrap(
        "reviews",
        showReviews,
        <ReviewsPart
          spherical={spherical}
          segments={segments}
          active={showReviews}
        />,
      )}
    </>
  );
}
